import React, { useCallback, useState } from "react";
import Card from "@components/cards/Card/Card";
import TextInput from "@components/inputs/TextInput/TextInput";
import Button from "@components/buttons/Button/Button";
import { sendSupportMessage } from "@services/supportService";
import { isRequiredField, isValidMaxLength } from "@util/validators";
import { MAX_SUPPORT_MESSAGE_LENGTH } from "@constants/validation";
import styles from "./SupportPage.module.css";

const INVALID_MESSAGE_ERROR = "Message is invalid. Please try again.";

const SupportPage = () => {
    const [message, setMessage] = useState("");
    const [messageErrorMessage, setMessageErrorMessage] = useState("");
    const [submitErrorMessage, setSubmitErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);

    // Message validation function
    const validateMessage = useCallback((val: string) => {
        const result = isRequiredField(val, "Message");
        if (result) {
            return result;
        }
        return isValidMaxLength(val, MAX_SUPPORT_MESSAGE_LENGTH);
    }, []);

    // Handle message change with validation
    const onMessageChange = useCallback((val: string) => {
        setMessage(val);
        setMessageErrorMessage(validateMessage(val));
    }, [validateMessage]);

    // Handle form submission
    const onSubmit = async () => {
        let isValid = true;

        // Validate message
        const messageValidationError = validateMessage(message);
        if (messageValidationError) {
            setMessageErrorMessage(messageValidationError);
            isValid = false;
        }

        if (isValid) {
            setSubmitErrorMessage("");
            setLoading(true); // Show loading during submit

            try {
                // Simulate the API call for submitting the support message
                await sendSupportMessage(message);

                // Optionally clear the form or show a success message
                setMessage("");
                setSubmitErrorMessage("");
                alert("Message sent successfully!"); // You can replace this with a more refined success handling
            } catch (error) {
                setSubmitErrorMessage("Failed to submit message. Please try again.");
            } finally {
                setLoading(false); // Hide loading
            }
        } else {
            setSubmitErrorMessage(INVALID_MESSAGE_ERROR);
        }
    };

    return (
        <div className={styles.container}>
            <h1>Support</h1>
            <p className={styles.description}>
                Give us a call, or leave us a message and we&apos;ll get back to you as soon as
                possible
            </p>
            <a href="tel:+611800978408" className={styles.phoneLink}>1800 978 408</a>
            <Card className={styles.cardContainer}>
                <TextInput
                    label="Message"
                    id="message"
                    value={message}
                    onChange={onMessageChange}
                    placeholder="Enter message..."
                    multiline
                    height="150px"
                    errorMessage={messageErrorMessage}
                    disabled={loading} // Disable input while loading
                />
                <Button
                    variant="primary"
                    onClick={onSubmit}
                    disabled={loading} // Disable button while loading
                >
                    {loading ? "Submitting..." : "Submit"}
                    {" "}
                    {/* Show loading text */}
                </Button>

                {submitErrorMessage && (
                    <p className={styles.errorMessage}>{submitErrorMessage}</p>
                )}
            </Card>
        </div>
    );
};

export default SupportPage;
